import React, { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import '../../css/ResetPassword.css';
import { DataContext } from "../shared/DataContext";

function ResetPassword() {
    const navigate = useNavigate();
    const { setCurrentUser } = useContext(DataContext);
    const { token } = useParams();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({
        password: '',
        confirmPassword: ''
    });

    const validatePassword = (password) => {
        if (password.length < 8) {
            return 'Password must be at least 8 characters long';
        }
        return '';
    };

    const validateConfirmPassword = (password, confirmPassword) => {
        if (password !== confirmPassword) {
            return 'Passwords do not match';
        }
        return '';
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'password') {
            setErrors(prev => ({
                ...prev,
                password: validatePassword(value),
                confirmPassword: validateConfirmPassword(value, formData.confirmPassword)
            }));
        } else if (name === 'confirmPassword') {
            setErrors(prev => ({
                ...prev,
                confirmPassword: validateConfirmPassword(formData.password, value)
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const passwordError = validatePassword(formData.password);
        const confirmPasswordError = validateConfirmPassword(
            formData.password,
            formData.confirmPassword
        );

        if (passwordError || confirmPasswordError) {
            setErrors({
                password: passwordError,
                confirmPassword: confirmPasswordError
            });
            return;
        }

        try {
            const data = {
                ...formData,
                token
            };
            const response = await axios.post('/api/reset_password', data);

            if (response.status !== 200) {
                throw new Error('Failed to reset password');
            }
            setCurrentUser(response.data);
            navigate(response.data.homepage);
        } catch (error) {
            setErrors(prev => ({
                ...prev,
                submit: 'Failed to reset password. Please try again.'
            }));
        }
    };

    const isFormValid = !errors.password &&
        !errors.confirmPassword &&
        formData.password &&
        formData.confirmPassword;

    return (
        <div className="resetPasswordContainer">
            <div className="resetPasswordFormWrapper">
                <h2 className="resetPasswordFormTitle">Reset Your Password</h2>

                <form onSubmit={handleSubmit} className="resetPasswordFormContainer">
                    <div className="resetPasswordPasswordRequirement">
                        Your password must be at least 8 characters:
                    </div>

                    <div className="resetPasswordInputGroup">
                        <label htmlFor="password" className="resetPasswordInputLabel">
                            Create a Password <span className="resetPasswordAsterisk">*</span>
                        </label>
                        <div className="resetPasswordInputWrapper">
                            <input
                                type="password"
                                id="password"
                                name="password"
                                className={`resetPasswordInput ${errors.password ? 'resetPasswordHasError' : ''}`}
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Enter Password"
                                required
                                aria-required="true"
                                aria-invalid={!!errors.password}
                                aria-describedby={errors.password ? "password-error" : undefined}
                            />
                            {errors.password && (
                                <span id="password-error" className="resetPasswordError">
                    {errors.password}
                  </span>
                            )}
                        </div>
                    </div>

                    <div className="resetPasswordInputGroup">
                        <label htmlFor="confirmPassword" className="resetPasswordInputLabel">
                            Confirm Password <span className="resetPasswordAsterisk">*</span>
                        </label>
                        <div className="resetPasswordInputWrapper">
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                className={`resetPasswordInput ${errors.confirmPassword ? 'resetPasswordHasError' : ''}`}
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="Confirm Password"
                                required
                                aria-required="true"
                                aria-invalid={!!errors.confirmPassword}
                                aria-describedby={errors.confirmPassword ? "confirm-password-error" : undefined}
                            />
                            {errors.confirmPassword && (
                                <span id="confirm-password-error" className="resetPasswordError">
                    {errors.confirmPassword}
                  </span>
                            )}
                        </div>
                    </div>

                    <div className="resetPasswordRequiredField">
                        <span className="resetPasswordAsterisk">*</span> Required
                    </div>

                    {errors.submit && (
                        <div className="resetPasswordError" role="alert">
                            {errors.submit}
                        </div>
                    )}

                    <button
                        type="submit"
                        className={`resetPasswordSubmitButton ${isFormValid ? 'active' : ''}`}
                        disabled={!isFormValid}
                    >
                        Reset Password
                    </button>
                </form>
            </div>
        </div>
    );
}

export default ResetPassword;
